import {
  DataDescription,
  newDataDescription,
} from '@decisions/server/DecisionsFramework/Design/Flow/Mapping/DataDescription';
import {
  getDataDescriptions,
  getCalculatedDataDescriptions,
  getReportImagesDataDescriptions,
} from '@decisions/server/DecisionsFramework/Design/Report/Service/ReportService';
import { setValue } from '@decisions/server/DecisionsFramework/Design/Properties/ContextProviderService';
import { getTreeNode } from 'components/EntityPicker/defaultEpickTreeModel';
import { EpickTreeConstants } from 'components/EntityPicker/entityPickerFetcher';
import { IEpickNode } from 'components/EntityPicker/EpickTree';
import { ITreeNode } from 'components/Tree/CommonTree/CommonTreeNode';
import { createDataPairValue } from 'utils/DataPairUtils';
import { getChildValueAsArray } from 'components/property-editors/utils/ProviderNodeUtils';
import { ContextProviderNode } from '@decisions/server/DecisionsFramework/Design/Properties';
import { isReportImageData } from '@decisions/server/DecisionsFramework/Design/Report';
import { newInlineField } from '@decisions/server/DecisionsFramework/Design/Report/InlineFields/InlineField';
import { DataPair } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ContextData';

export interface IDataDescriptionFetcher {
  fetchDataDescriptions: (value?: string) => Promise<DataDescription[]>;
  fetchCalculatedData: (value?: string) => Promise<string[]>;
}

export function dataDescriptionFetcher(reportContextId: string, isImageDataLoader: boolean): IDataDescriptionFetcher {
  const fetchDataDescriptions = (path?: string): Promise<DataDescription[]> => {
    return getDataDescriptions({ reportContextId, path });
  };

  const fetchCalculatedData = (): Promise<string[]> => {
    if (isImageDataLoader) return getReportImagesDataDescriptions({ reportContextId });
    return getCalculatedDataDescriptions({ reportContextId });
  };

  return { fetchDataDescriptions, fetchCalculatedData };
}

export function dataDescriptionTreeModel(
  { fetchDataDescriptions, fetchCalculatedData }: IDataDescriptionFetcher,
  isImageDataLoader: boolean
) {
  const getRootNodes = async (): Promise<ITreeNode<IEpickNode>[]> => {
    const dataDescriptionRootNode: IEpickNode = {
      id: EpickTreeConstants.reportRootFolderId,
      name: EpickTreeConstants.reportRootFolderName,
      expanded: true,
      expandable: true,
    };

    let dataDescriptionNodes: DataDescription[] = await fetchDataDescriptions();
    if (isImageDataLoader) dataDescriptionNodes = dataDescriptionNodes.filter(item => isReportImageData(item));
    let rootNodes: Array<ITreeNode<IEpickNode>> = [];
    rootNodes.push({
      ...getTreeNode(dataDescriptionRootNode),
      childNodes: dataDescriptionNodes.map(dd => getDataDescriptionNode(dd)),
    } as ITreeNode<IEpickNode>);

    const hasCalculatedNode = await hasCaculatedData(fetchCalculatedData);

    if (hasCalculatedNode) {
      const calculatedDataRootNode: IEpickNode = {
        id: EpickTreeConstants.calculatedFieldNodeName,
        name: EpickTreeConstants.calculatedFieldNodeName,
        expanded: false,
        expandable: true,
      };
      rootNodes.push({
        ...getTreeNode(calculatedDataRootNode),
      } as ITreeNode<IEpickNode>);
    }

    return rootNodes;
  };

  const getSubNodes = async (nodeId: string): Promise<ITreeNode<IEpickNode>[]> => {
    if (nodeId === EpickTreeConstants.calculatedFieldNodeName) {
      const inlineField = newInlineField();
      let calculatedDataNodes: string[] = await fetchCalculatedData(nodeId);
      return calculatedDataNodes.map(node =>
        getDataDescriptionNode(
          newDataDescription({
            Name: node,
            Title: node,
            DisplayName: nodeId,
            FullTypeName: inlineField.__type,
            Type: inlineField,
            HasReadableSubElements: false,
          }),
          nodeId
        )
      );
    }

    let dataDescriptionNodes: DataDescription[] = await fetchDataDescriptions(nodeId);

    if (isImageDataLoader) dataDescriptionNodes = dataDescriptionNodes.filter(item => isReportImageData(item));

    return dataDescriptionNodes.map(dd => getDataDescriptionNode(dd, nodeId));
  };

  return { getRootNodes, getSubNodes };
}

export async function submit(editingContext: string, path: string[], item: IEpickNode, isImageDataLoader: boolean) {
  let value: DataPair;
  if (isImageDataLoader) value = createDataPairValue(item.name);
  else value = createDataPairValue([item.id, item.name]);

  await setValue({
    contextProviderRegistrationId: editingContext,
    path,
    value,
  });
}

export function getPickedEntityName(node: ContextProviderNode): string {
  let [value] = getChildValueAsArray(node);
  return value !== null ? value : '';
}

function getDataDescriptionNode({ Name, Title, Type, HasReadableSubElements }: DataDescription, parentPath?: string) {
  let id = Name;
  if (parentPath) {
    id = `${parentPath}.${Name}`;
  }

  let node = getTreeNode({
    id,
    name: Title,
    pickable: true,
    expandable: HasReadableSubElements,
  });

  if (node.data) {
    return node;
  }

  node.description = Type.Name;
  return node;
}

async function hasCaculatedData(fetchCalculatedData: (value?: string) => Promise<string[]>): Promise<boolean> {
  let data: string[] = await fetchCalculatedData();
  if (data && data.length) return true;
  return false;
}
